import { QuickstartCard } from './QuickstartCard';
import { QuickstartTheme, getResponsiveTextSize } from '../types/theme';
import { useIsLandscape } from '../utils/responsiveHooks';

interface QuickstartMatchStatusProps {
  theme: QuickstartTheme;
  /** Headline, e.g. "Match Ended" or "Matchmaking Failed" */
  title: string;
  /** Detail line, e.g. the backend-provided reason */
  message?: string;
  /** When provided, renders a requeue button (MATCH_FAILED canRetry / play-again) */
  onRetry?: () => void;
  retryLabel?: string;
}

/**
 * QuickstartMatchStatus Component
 *
 * Neutral end-of-match surface: a themed card with a headline, an optional
 * detail line, and an optional requeue button. Used for the v2 match-ended
 * screen and for retryable matchmaking failures.
 */
export const QuickstartMatchStatus = ({ theme, title, message, onRetry, retryLabel = 'Play Again' }: QuickstartMatchStatusProps) => {
  const isLandscape = useIsLandscape();

  return (
    <div
      className='flex items-center justify-center overflow-auto'
      style={{
        minHeight: '100vh',
        backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: '1rem',
      }}
    >
      <QuickstartCard theme={theme}>
        <div className={`flex flex-col items-center text-center ${isLandscape ? 'space-y-3' : 'space-y-6'}`}>
          <div
            className={`font-["Inter"] ${getResponsiveTextSize('3xl', isLandscape)} font-semibold`}
            style={{ color: theme.primaryColor }}
          >
            {title}
          </div>

          {message && (
            <p
              className={`max-w-[280px] ${getResponsiveTextSize('base', isLandscape)}`}
              style={{ color: '#FEF3C7' }}
            >
              {message}
            </p>
          )}

          {onRetry && (
            <button
              onClick={onRetry}
              className={`rounded-lg font-["Inter"] font-semibold ${isLandscape ? 'px-4 py-2' : 'px-6 py-3'} ${getResponsiveTextSize('lg', isLandscape)} transition-opacity hover:opacity-90`}
              style={{
                backgroundColor: theme.primaryColor,
                color: theme.modalBackgroundColor,
              }}
            >
              {retryLabel}
            </button>
          )}
        </div>
      </QuickstartCard>
    </div>
  );
};
